import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Check,
  ImageUp,
  Mic,
  Monitor,
  Plus,
  RectangleHorizontal,
  RectangleVertical,
  Sparkles,
} from 'lucide-react';
import { Badge, Button, Card, cn } from '@pixlide/ui';
import { useCreateCustomOrder } from '@/api/queries/custom-orders';
import type { components } from '@/api/generated/schema';

type VideoFormat = components['schemas']['PixlideDomainContentEnumsVideoFormat'];

const SCREEN_COUNTS = [1, 2, 3, 4, 5, 6, '7+'] as const;

type Orientation = 'landscape' | 'portrait' | 'other';

/**
 * Mapping vers `VideoFormat` (enum numérique 0-3 côté API, sans documentation de valeurs
 * dans le schéma généré). Seules les deux orientations qui correspondent clairement à un
 * format vidéo sont envoyées ; « Autre » n'a pas d'équivalent évident et reste local à
 * l'écran en attendant que l'API précise ce cas.
 */
const FORMAT_BY_ORIENTATION: Partial<Record<Orientation, VideoFormat>> = {
  landscape: 0,
  portrait: 1,
};

interface ProOption {
  key: string;
  label: string;
  description: string;
}

const PRO_OPTIONS: ProOption[] = [
  {
    key: 'weekend',
    label: 'Week-end',
    description: 'Recevez votre projet les jours non ouvrés.',
  },
  {
    key: 'revisions',
    label: 'Pack révisions Premium',
    description: 'Passez de 2 à 5 retours pour peaufiner votre vidéo sans stress.',
  },
  {
    key: 'artDirection',
    label: 'Inclure une Direction Artistique',
    description: 'Nous vous aidons à partir de zéro pour créer votre visuel.',
  },
  {
    key: 'localization',
    label: 'Ajouter une déclinaison linguistique',
    description: 'Ajouter une version dans une autre langue.',
  },
];

function Toggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative h-6 w-11 shrink-0 rounded-pill transition-colors',
        checked ? 'bg-ink-900' : 'bg-ink-200',
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 left-0 size-5 rounded-full bg-white shadow transition-transform',
          checked ? 'translate-x-[22px]' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

function OrientationOption({
  value,
  selected,
  icon,
  label,
  onSelect,
}: {
  value: Orientation;
  selected: boolean;
  icon: React.ReactNode;
  label: string;
  onSelect: (value: Orientation) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      aria-pressed={selected}
      className={cn(
        'relative flex flex-1 flex-col items-center gap-3 rounded-xl bg-white px-6 py-5 text-sm font-medium ring-1 transition',
        selected ? 'bg-ink-900 text-white ring-ink-900' : 'text-ink-700 ring-ink-100 hover:ring-ink-200',
      )}
    >
      <span aria-hidden className={selected ? 'text-white' : 'text-ink-500'}>
        {icon}
      </span>
      {label}
      <span
        aria-hidden
        className={cn(
          'absolute top-4 right-4 grid size-4 place-items-center rounded-full ring-1',
          selected ? 'bg-white ring-white' : 'ring-ink-200',
        )}
      >
        {selected ? <Check className="size-3 text-ink-900" strokeWidth={3} /> : null}
      </span>
    </button>
  );
}

/**
 * Formulaire de brief pour une création sur-mesure, réalisée par l'équipe Pixlide sous 48h.
 *
 * Reprend la maquette du board produit. Seuls le nom du projet, le format (paysage/portrait)
 * et la description partent réellement vers `/projects/custom-orders` : le reste du brief
 * (nombre d'écrans, options « Version pro », pièces jointes) n'a pas d'équivalent dans la
 * commande API actuelle et reste local à l'écran, comme un complément de contexte visuel.
 */
export function CustomOrderPage() {
  const navigate = useNavigate();
  const createCustomOrder = useCreateCustomOrder();

  const [projectName, setProjectName] = useState('');
  const [description, setDescription] = useState('');
  const [screenCount, setScreenCount] = useState<(typeof SCREEN_COUNTS)[number]>(4);
  const [screensLinked, setScreensLinked] = useState(true);
  const [orientation, setOrientation] = useState<Orientation>('landscape');
  const [proOptions, setProOptions] = useState<Record<string, boolean>>({
    weekend: true,
    revisions: false,
    artDirection: false,
    localization: false,
  });

  function resetForm() {
    setProjectName('');
    setDescription('');
    setScreenCount(4);
    setOrientation('landscape');
    setProOptions({ weekend: true, revisions: false, artDirection: false, localization: false });
  }

  async function handleSubmit() {
    const format = FORMAT_BY_ORIENTATION[orientation] ?? FORMAT_BY_ORIENTATION.landscape!;

    await createCustomOrder.mutateAsync({
      name: projectName.trim() || 'Nouveau projet sur-mesure',
      format,
      brief: description.trim(),
    });

    await navigate({ to: '/creer' });
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Création sur-mesure</h1>
        <p className="mt-1.5 text-ink-500">
          Vous avez une charte graphique, déjà un visuel à travailler. Remplissez les informations
          suivantes
        </p>
      </div>

      <ol className="flex items-center justify-center gap-3 text-sm font-medium text-ink-300">
        <li className="flex items-center gap-1.5 text-ink-900">
          <span
            aria-hidden
            className="grid size-5 place-items-center rounded-full bg-ink-900 text-white"
          >
            <Check className="size-3" strokeWidth={3} />
          </span>
          Configuration
        </li>
        <span aria-hidden>{'>'}</span>
        <li className="flex items-center gap-1.5">
          <span aria-hidden className="grid size-5 place-items-center rounded-full ring-1 ring-ink-200">
            2
          </span>
          Validation
        </li>
      </ol>

      <Card raised className="flex flex-col gap-6 p-7">
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="flex items-center gap-2 rounded-xl bg-white px-3.5 py-2.5 ring-1 ring-ink-100 focus-within:ring-2 focus-within:ring-ink-900">
            <input
              value={projectName}
              onChange={(event) => setProjectName(event.target.value)}
              placeholder="Nommer votre projet"
              aria-label="Nommer votre projet"
              className="min-w-0 flex-1 bg-transparent text-sm placeholder:text-ink-300 focus-visible:outline-none"
            />
            {projectName.trim() !== '' ? (
              <Check aria-hidden className="size-4 shrink-0 text-ink-900" />
            ) : null}
          </div>

          <div className="flex items-center gap-2 rounded-xl bg-white px-3.5 py-2.5 text-sm font-medium ring-1 ring-ink-100">
            {/* Type d'établissement : verrouillé à « Restauration », seul secteur couvert
                pour l'instant côté produit. */}
            <span className="flex-1">Restauration</span>
          </div>
        </div>

        <textarea
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          placeholder="Description"
          aria-label="Description"
          rows={3}
          className="rounded-xl bg-white px-3.5 py-3 text-sm ring-1 ring-ink-100 placeholder:text-lg placeholder:text-ink-300 focus-visible:outline-none focus-within:ring-2 focus-within:ring-ink-900"
        />

        <div className="flex flex-wrap items-center gap-5 text-sm font-medium text-ink-700">
          <button type="button" className="flex items-center gap-1.5">
            <Plus aria-hidden className="size-4" />
            Ajouter un document
          </button>
          <button type="button" className="flex items-center gap-1.5">
            <Sparkles aria-hidden className="size-4" />
            Générer avec l&apos;IA
          </button>
          <button type="button" aria-label="Dicter la description" className="text-ink-500">
            <Mic aria-hidden className="size-4" />
          </button>
        </div>

        <label className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-ink-200 bg-white/60 px-6 py-10 text-center text-sm text-ink-500">
          <ImageUp aria-hidden className="size-6 text-ink-300" />
          Déposer vos éléments ici, ou
          <span className="rounded-xl px-4 py-2 text-sm font-medium ring-1 ring-ink-200">
            Cliquez pour parcourir
          </span>
          <input type="file" multiple className="sr-only" />
        </label>

        <div>
          <h2 className="mb-3 text-sm font-semibold">Nombre d&apos;écrans</h2>
          <div className="flex flex-wrap gap-2">
            {SCREEN_COUNTS.map((count) => (
              <button
                key={count}
                type="button"
                onClick={() => setScreenCount(count)}
                aria-pressed={screenCount === count}
                className={cn(
                  'grid size-10 place-items-center rounded-lg text-sm font-medium ring-1 transition',
                  screenCount === count
                    ? 'bg-ink-900 text-white ring-ink-900'
                    : 'text-ink-700 ring-ink-100 hover:ring-ink-200',
                )}
              >
                {count}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => setScreensLinked(true)}
            aria-pressed={screensLinked}
            className={cn(
              'rounded-xl px-4 py-2.5 text-sm font-medium ring-1 transition',
              screensLinked ? 'bg-ink-900 text-white ring-ink-900' : 'text-ink-700 ring-ink-100',
            )}
          >
            Lier tous les écrans
          </button>
          <button
            type="button"
            onClick={() => setScreensLinked(false)}
            aria-pressed={!screensLinked}
            className={cn(
              'rounded-xl px-4 py-2.5 text-sm font-medium ring-1 transition',
              !screensLinked ? 'bg-ink-900 text-white ring-ink-900' : 'text-ink-700 ring-ink-100',
            )}
          >
            Dissocier tous les écrans
          </button>
        </div>

        <div>
          <h2 className="mb-3 text-sm font-semibold">Orientation des écrans</h2>
          <div className="flex gap-3">
            <OrientationOption
              value="landscape"
              selected={orientation === 'landscape'}
              icon={<RectangleHorizontal className="size-6" strokeWidth={1.25} />}
              label="Paysage"
              onSelect={setOrientation}
            />
            <OrientationOption
              value="portrait"
              selected={orientation === 'portrait'}
              icon={<RectangleVertical className="size-6" strokeWidth={1.25} />}
              label="Portrait"
              onSelect={setOrientation}
            />
            <OrientationOption
              value="other"
              selected={orientation === 'other'}
              icon={<Monitor className="size-6" strokeWidth={1.25} />}
              label="Autre"
              onSelect={setOrientation}
            />
          </div>
        </div>

        <div className="flex flex-col divide-y divide-ink-100">
          {PRO_OPTIONS.map((option) => (
            <div key={option.key} className="flex items-center justify-between gap-4 py-4">
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold">{option.label}</p>
                  <Badge tone="brand">
                    <span className="tracking-wide uppercase">Version pro</span>
                  </Badge>
                </div>
                <p className="mt-1 text-sm text-ink-500">{option.description}</p>
              </div>

              <Toggle
                checked={proOptions[option.key] ?? false}
                onChange={(checked) =>
                  setProOptions((current) => ({ ...current, [option.key]: checked }))
                }
                label={option.label}
              />
            </div>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3 pt-2">
          <Button variant="ghost" type="button" onClick={resetForm}>
            Tout effacer
          </Button>
          <Button
            type="button"
            isLoading={createCustomOrder.isPending}
            onClick={handleSubmit}
          >
            Continuer
          </Button>
        </div>

        <button
          type="button"
          onClick={() => navigate({ to: '/creer' })}
          className="mx-auto text-sm text-ink-500 hover:text-ink-900"
        >
          Annuler
        </button>
      </Card>
    </div>
  );
}
